import { keyframes, style } from '@vanilla-extract/css';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { fillScope } from '@customerio/pluma-components/css/utils';

const STAGE_SIZE = '460px';
const BLOOM_SIZE = '380px';
const BLOOM_BLUR = '70px';
const BLOOM_TINT_PRIMARY = '45%';
const BLOOM_TINT_SECONDARY = '65%';

const bloomTint = (strength: string) =>
  `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${themeVars.palette['teal-500']} ${strength}, transparent), transparent 70%)`;

const PATH_X_RATIO = 0.7;
const PATH_Y_RATIO = 0.1;
const PATH_SAMPLES = 16;
const SCALE_MIN = 0.94;
const SCALE_SPAN = 0.16;

const pathX = `calc(${STAGE_SIZE} * ${PATH_X_RATIO})`;
const pathY = `calc(${STAGE_SIZE} * ${PATH_Y_RATIO})`;

const bloomPath = keyframes(
  Object.fromEntries(
    Array.from({ length: PATH_SAMPLES + 1 }, (_, i) => {
      const t = (i / PATH_SAMPLES) * Math.PI * 2;
      const x = Math.sin(t).toFixed(4);
      const y = Math.sin(2 * t).toFixed(4);
      const scale = (SCALE_MIN + SCALE_SPAN * Math.abs(Math.sin(t))).toFixed(3);
      const offset = +((i / PATH_SAMPLES) * 100).toFixed(4);
      return [
        `${offset}%`,
        { transform: `translate3d(calc(${pathX} * ${x}), calc(${pathY} * ${y}), 0) scale(${scale})` },
      ];
    }),
  ),
);

const bloomBreathe = keyframes({
  '0%': { opacity: 0.55 },
  '50%': { opacity: 1 },
  '100%': { opacity: 0.55 },
});

const markBreathe = keyframes({
  '0%': { transform: 'scale(1)' },
  '50%': { transform: 'scale(1.06)' },
  '100%': { transform: 'scale(1)' },
});

const segmentPulse = keyframes({
  '0%, 100%': { opacity: 0.35 },
  '40%': { opacity: 1 },
});

const screenFadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

// Most post-signup waits resolve in well under this, so the loader stays
// invisible and the fast path shows no flash at all.
const APPEAR_DELAY_MS = 300;
const APPEAR_FADE_MS = 200;

// Layout only — the variants below own height and background, so neither
// re-declares anything here and the composed rule can't defeat this media query.
const screenBase = style(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: themeVars.space['400'],
    overflow: 'hidden',
    position: 'relative',
  },
  'brand-loader-screen',
);

export const screenDelayedAppear = style(
  {
    // `backwards`, not `forwards`: the fill must not outlive the animation, or
    // it would beat the inline opacity an AnimatePresence exit sets on unmount.
    animation: `${screenFadeIn} ${APPEAR_FADE_MS}ms ease-out ${APPEAR_DELAY_MS}ms backwards`,
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        animation: `${screenFadeIn} 1ms linear ${APPEAR_DELAY_MS}ms backwards`,
      },
    },
  },
  'brand-loader-screen-delayed-appear',
);

/** Owns the viewport: the loader is the whole page. */
export const screenStandalone = style(
  [
    screenBase,
    {
      minHeight: '100dvh',
      backgroundColor: fillScope('neutral', 'subtle'),
    },
  ],
  'brand-loader-screen-standalone',
);

/**
 * Fills a host that already sizes and paints itself. Without `width`, a flex
 * parent sizes this to its content and the background paints as a narrow band
 * instead of the page; without dropping `100dvh`, a host that is already
 * viewport-tall overflows into page scroll.
 */
export const screenEmbedded = style(
  [
    screenBase,
    {
      width: '100%',
      alignSelf: 'stretch',
    },
  ],
  'brand-loader-screen-embedded',
);

export const stage = style(
  {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: STAGE_SIZE,
    height: STAGE_SIZE,
  },
  'brand-loader-stage',
);

const bloomBase = style(
  {
    position: 'absolute',
    width: BLOOM_SIZE,
    height: BLOOM_SIZE,
    borderRadius: '50%',
    filter: `blur(${BLOOM_BLUR})`,
    pointerEvents: 'none',
    willChange: 'transform, opacity',
  },
  'brand-loader-bloom',
);

// Re-declared per bloom rather than on `bloomBase`: the composed style sets
// `animation` unconditionally at equal specificity, so a reset on the base is
// emitted first and loses.
const bloomReducedMotion = {
  '@media': {
    '(prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 0.75,
      transform: 'none',
      willChange: 'auto',
    },
  },
} as const;

export const bloomPrimary = style(
  [
    bloomBase,
    {
      background: bloomTint(BLOOM_TINT_PRIMARY),
      animation: `${bloomPath} 4.4s linear infinite, ${bloomBreathe} 5s ease-in-out infinite`,
      ...bloomReducedMotion,
    },
  ],
  'brand-loader-bloom-primary',
);

export const bloomSecondary = style(
  [
    bloomBase,
    {
      background: bloomTint(BLOOM_TINT_SECONDARY),
      animation: `${bloomPath} 9.1s linear -3.2s infinite, ${bloomBreathe} 11s ease-in-out infinite`,
      ...bloomReducedMotion,
    },
  ],
  'brand-loader-bloom-secondary',
);

export const mark = style(
  {
    position: 'relative',
    color: themeVars.palette['teal-700'],
    animation: `${markBreathe} 4s ease-in-out infinite`,
    willChange: 'transform',
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
  },
  'brand-loader-mark',
);

export const markSegment = style(
  {
    fill: 'currentColor',
    animation: `${segmentPulse} 1.8s ease-in-out infinite`,
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        animation: 'none',
        opacity: 1,
      },
    },
  },
  'brand-loader-mark-segment',
);

export const markSegmentDelay1 = style({ animationDelay: '0.2s' }, 'brand-loader-mark-delay-1');
export const markSegmentDelay2 = style({ animationDelay: '0.4s' }, 'brand-loader-mark-delay-2');
